/**
 * What a chip is allowed to show at a given width.
 *
 * The old rule was structural — three lanes or more and the chip dropped to the
 * time alone — which got the priority exactly backwards. The time is the LEAST
 * useful thing on a chip (the row is already sitting at its own y position on
 * the hour ruler, so the time is largely redundant), and the thumbnail is the
 * MOST useful (it identifies the send at a glance, with no reading). So the
 * order things appear as a chip widens is image → text → time, and the order
 * they are shed as it narrows is the reverse.
 */

export interface ChipContentInput {
    /** The chip's rendered width in px. See `laneWidthPx` in time-layout.ts. */
    widthPx: number;
    /** Only an event that actually has an asset can spend width on one. */
    hasImage: boolean;
}

export interface ChipContentPlan {
    image: boolean;
    /** The title, and with it the kind icon / step / repeat glyphs that frame it. */
    text: boolean;
    time: boolean;
}

/**
 * Every plan this module hands out is one of eight frozen objects, so the SAME
 * answer is always the SAME reference.
 *
 * That is a rendering contract, not a micro-optimisation: the time grid plans
 * every chip on every render, and a drag re-renders the grid on every pointer
 * move. A freshly built `{ image, text, time }` is a new prop each time, which
 * defeats the chip's React.memo — measured live, one drag across a dense week
 * re-rendered all 55 chips on every move. An interned plan only changes when the
 * answer does. Frozen so a caller cannot edit a plan every other chip shares.
 */
const PLANS: readonly ChipContentPlan[] = Array.from({ length: 8 }, (_, bits) => Object.freeze({
    image: (bits & 4) !== 0,
    text: (bits & 2) !== 0,
    time: (bits & 1) !== 0,
}));

function planOf(image: boolean, text: boolean, time: boolean): ChipContentPlan {
    return PLANS[(image ? 4 : 0) | (text ? 2 : 0) | (time ? 1 : 0)];
}

/**
 * The invariant every plan in this module must satisfy: a chip ALWAYS renders
 * something. A blank chip surrounded by chips with pictures does not read as
 * "this one is narrow" — it reads as an image that failed to load, which is the
 * single worst thing the grid can say about a send that is perfectly fine.
 */
function neverEmpty(plan: ChipContentPlan): ChipContentPlan {
    if (plan.image || plan.text || plan.time) return planOf(plan.image, plan.text, plan.time);
    // Text is the highest-priority thing that is always available (an event
    // without a title still has one — the empty string ellipsises to nothing,
    // but the chip keeps its shape). A clipped word beats a blank box.
    return planOf(false, true, false);
}

/** Mirrors `.chipTimeSize .chipThumb` width in calendar-grid.module.scss: the
 *  thumbnail is flush to the trailing edge and takes this much of the chip. */
export const THUMB_WIDTH_PX = 38;

/** Rail (3px) + the chip's inline padding + the gap between two parts. Width
 *  that never belongs to content, whatever the chip ends up showing. */
const CHROME_PX = 20;

/** Below this the thumbnail is a smudge rather than a picture, so it is not
 *  worth the width it costs the rest of the chip. */
const IMAGE_MIN_PX = 24;

/** A title shorter than this is one ellipsis and nothing else. */
const TEXT_MIN_PX = 44;

/** "20:11" at 11px/600 tabular (~26px) plus the gap that separates it from the
 *  title. Deliberately a little generous: the cost of over-reserving is that a
 *  borderline chip sheds its time, which is the least useful thing on it. */
const TIME_MIN_PX = 36;

/**
 * Decide what a chip of this width renders. Pure: the caller supplies the width
 * (derived from the lane layout, not measured per chip) and this owns every
 * threshold, so no component carries a magic number.
 */
export function planChipContent({ widthPx, hasImage }: ChipContentInput): ChipContentPlan {
    const width = Number.isFinite(widthPx) ? Math.max(0, widthPx) : 0;

    const image = hasImage && width >= IMAGE_MIN_PX;
    const free = width - CHROME_PX - (image ? THUMB_WIDTH_PX : 0);

    const text = free >= TEXT_MIN_PX;
    // Time is the first thing to go, so it never appears without the title it
    // sits next to — a chip showing a bare time is the bug this replaces.
    const time = text && free >= TEXT_MIN_PX + TIME_MIN_PX;

    return neverEmpty({ image, text, time });
}

/**
 * The same plan for an occurrence whose picture turned out to be unusable — no
 * asset at all, or an `<img>` whose src failed to load (an expired or dead asset
 * URL). Pure, and deliberately width-free: it re-spends the image's room on the
 * next priority down rather than re-deriving thresholds, so this stays the only
 * module that owns a number.
 *
 * `time` is carried through untouched: it was already affordable alongside the
 * image, and freeing the image's width cannot make it less so.
 */
export function planWithoutImage(plan: ChipContentPlan): ChipContentPlan {
    if (!plan.image) return neverEmpty(plan);
    return planOf(false, true, plan.time);
}
